import React, { useState, useEffect } from 'react';
import AuthErrorMessage from '../messages/AuthErrorMessage';
import AuthFormLayout from '../layouts/AuthFormLayout';
import AuthNavButton from '../common/AuthNavButton';
import { ProfileType } from '../../../../types/profileTypes';
import { useUsernameUniqueness } from '../../../../services/usernameUniqueness/hooks/useUsernameUniqueness';
import UsernameEmailGroup from '../groups/UsernameEmailGroup';
import PasswordGroup from '../groups/PasswordGroup';
import LocationGroup from '../groups/LocationGroup';
import ProfileTypeGroup from '../groups/ProfileTypeGroup';

interface SignUpFormProps {
  onSubmit: (
    email: string,
    password: string,
    address: string,
    preferredUsername: string,
    profileType: ProfileType
  ) => Promise<void>;
  onSwitchToSignIn: () => void;
  onClearError: () => void;
  isLoading: boolean;
  error?: string | null;
}

const SignUpForm: React.FC<SignUpFormProps> = ({
  onSubmit,
  onSwitchToSignIn,
  isLoading,
  error,
  onClearError,
}) => {
  // Login form fields
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // User Attribute form fields
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [address, setAddress] = useState('');
  const [username, setUsername] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [profileType, setProfileType] = useState<ProfileType | ''>('');

  const { isUnique: usernameUnique, loading: checkingUsername } = useUsernameUniqueness(username);

  // Add state to track focus for each field
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [confirmPasswordFocused, setConfirmPasswordFocused] = useState(false);
  const [cityFocused, setCityFocused] = useState(false);
  const [stateFocused, setStateFocused] = useState(false);

  // Helper to determine status for each field
  const getUsernameStatus = () => {
    if (username && usernameUnique === false) return 'error';
    if (username && usernameUnique === true) return 'success';
    return 'default';
  };
  const getEmailStatus = () => {
    if (email && emailError) return 'error';
    if (email && !emailError) return 'success';
    return 'default';
  };
  const getPasswordStatus = () => {
    if (passwordFocused) return 'focus';
    if (password && !isPasswordValid(password)) return 'error';
    if (password && isPasswordValid(password)) return 'success';
    return 'default';
  };
  const getConfirmPasswordStatus = () => {
    if (confirmPasswordFocused) return 'focus';
    if (confirmPassword && password !== confirmPassword) return 'error';
    if (confirmPassword && password === confirmPassword) return 'success';
    return 'default';
  };
  const getCityStatus = () => (cityFocused ? 'focus' : 'default');
  const getStateStatus = () => (stateFocused ? 'focus' : 'default');

  const handleUsernameChange = (value: string) => {
    setUsername(value.replace(/[^a-zA-Z]/g, '').toLowerCase());
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    onClearError();
  };

  const validEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email) && email.length <= 254;
  };

  useEffect(() => {
    if (!checkingUsername && username && !usernameUnique) {
      setUsernameError('Username is already taken');
    } else {
      setUsernameError('');
    }
  }, [checkingUsername, username, usernameUnique]);

  useEffect(() => {
    if (email && !validEmail(email)) {
      setEmailError('Invalid email address format');
    } else {
      setEmailError('');
    }
  }, [email]);

  useEffect(() => {
    if (confirmPassword && password !== confirmPassword) {
      setPasswordError('Passwords do not match');
    } else {
      setPasswordError('');
    }
  }, [password, confirmPassword]);

  useEffect(() => {
    setAddress(
      JSON.stringify({
        locality: city,
        region: state,
      })
    );
  }, [city, state]);

  // Add a function to check if password requirements are met
  function isPasswordValid(pw: string): boolean {
    return (
      pw.length >= 8 &&
      /[A-Z]/.test(pw) &&
      /[a-z]/.test(pw) &&
      /[0-9]/.test(pw) &&
      /[^A-Za-z0-9]/.test(pw)
    );
  }

  const isFormValid =
    !!email &&
    !emailError &&
    !!password &&
    !passwordError &&
    isPasswordValid(password) &&
    !!address &&
    !!city &&
    !!state &&
    !!username &&
    !!profileType &&
    usernameUnique === true; // Only allow if username is unique

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (emailError || passwordError) return;
    if (!address || !email || !username || !profileType) return;
    await onSubmit(email, password, address, username, profileType);
  };

  return (
    <AuthFormLayout
      title="Sign up"
      footer={
        <div className="mt-4 text-center">
          <AuthNavButton onClick={onSwitchToSignIn}>Already have an account? Sign in</AuthNavButton>
        </div>
      }
      onSubmit={handleSubmit}
    >
      <UsernameEmailGroup
        username={username}
        setUsername={handleUsernameChange}
        usernameStatus={getUsernameStatus()}
        usernameError={usernameError}
        checkingUsername={checkingUsername}
        usernameUnique={usernameUnique}
        email={email}
        setEmail={handleEmailChange}
        emailStatus={getEmailStatus()}
        emailError={emailError}
      />
      <PasswordGroup
        password={password}
        setPassword={setPassword}
        passwordStatus={getPasswordStatus()}
        confirmPassword={confirmPassword}
        setConfirmPassword={setConfirmPassword}
        confirmPasswordStatus={getConfirmPasswordStatus()}
        passwordError={passwordError}
        passwordFocused={passwordFocused}
        setPasswordFocused={setPasswordFocused}
        confirmPasswordFocused={confirmPasswordFocused}
        setConfirmPasswordFocused={setConfirmPasswordFocused}
      />
      <LocationGroup
        city={city}
        setCity={setCity}
        cityStatus={getCityStatus()}
        cityFocused={cityFocused}
        setCityFocused={setCityFocused}
        state={state}
        setState={setState}
        stateStatus={getStateStatus()}
        stateFocused={stateFocused}
        setStateFocused={setStateFocused}
      />
      <ProfileTypeGroup
        profileType={profileType}
        setProfileType={setProfileType}
        isFormValid={isFormValid}
        isLoading={isLoading}
      />
      <AuthErrorMessage error={error} />
    </AuthFormLayout>
  );
};

export default SignUpForm;
